import React, { useState } from 'react';
import apiService, { PerformanceComparison, MultiTimeframeComparison, SparklineData } from '../services/api';
import SparklineChart from '../components/SparklineChart';

const PerformanceComparisonPage: React.FC = () => {
  const [skuInput, setSkuInput] = useState('');
  const [timeframe, setTimeframe] = useState(7);
  const [performanceData, setPerformanceData] = useState<PerformanceComparison[]>([]);
  const [multiTimeframeData, setMultiTimeframeData] = useState<MultiTimeframeComparison[]>([]);
  const [sparklineData, setSparklineData] = useState<SparklineData>({});
  const [viewMode, setViewMode] = useState<'single' | 'multi'>('single');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timeframeOptions = [
    { value: 7, label: '7 dias' },
    { value: 14, label: '14 dias' },
    { value: 21, label: '21 dias' },
    { value: 28, label: '28 dias' },
    { value: 56, label: '56 dias' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Parse SKUs from input (comma or newline separated)
    const skus = skuInput
      .split(/[,\n]/)
      .map(sku => sku.trim())
      .filter(sku => sku.length > 0);

    if (skus.length === 0) {
      setError('Por favor, insira pelo menos um SKU');
      return;
    }

    setLoading(true);
    setError(null);
    setPerformanceData([]);
    setMultiTimeframeData([]);
    setSparklineData({});

    try {
      if (viewMode === 'single') {
        const data = await apiService.getPerformanceComparison({
          skus,
          timeframe_days: timeframe
        });
        setPerformanceData(data);
      } else {
        // Get both multi-timeframe data and sparkline data in parallel
        const [multiData, sparkData] = await Promise.all([
          apiService.getMultiTimeframeComparison(skus),
          apiService.getSparklineData(skus)
        ]);
        setMultiTimeframeData(multiData);
        setSparklineData(sparkData);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao buscar dados de performance');
      setPerformanceData([]);
      setMultiTimeframeData([]);
      setSparklineData({});
    } finally {
      setLoading(false);
    }
  };

  const formatPercentage = (value: number) => {
    const formattedValue = value.toFixed(1);
    if (value > 0) {
      return <span className="text-success">+{formattedValue}%</span>;
    } else if (value < 0) {
      return <span className="text-danger">{formattedValue}%</span>;
    }
    return <span className="text-muted">{formattedValue}%</span>;
  };

  const formatPricePercentage = (value: number) => {
    const formattedValue = value.toFixed(1);
    if (value > 0) {
      return <span className="text-danger">+{formattedValue}%</span>; // Preço subiu = pior = vermelho
    } else if (value < 0) {
      return <span className="text-success">{formattedValue}%</span>; // Preço desceu = melhor = verde
    }
    return <span className="text-muted">{formattedValue}%</span>;
  };

  const formatCurrency = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '0.00€';
    return numValue.toFixed(2) + '€';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-PT');
  };

  const getTrendIndicator = (shortTerm: number, longTerm: number) => {
    const isImproving = shortTerm > longTerm;
    const bothPositive = shortTerm > 0 && longTerm > 0;
    const bothNegative = shortTerm < 0 && longTerm < 0;
    const diff = Math.abs(shortTerm - longTerm);
    
    // Se a diferença é muito pequena, considerar estável
    if (diff < 5) {
      return { 
        icon: '→', 
        color: 'text-muted', 
        text: 'Estável', 
        rowClass: 'table-secondary',
        detail: `7d: ${shortTerm.toFixed(1)}% | 28d: ${longTerm.toFixed(1)}%`
      };
    }
    
    // Ambos positivos
    if (bothPositive && isImproving) {
      return { 
        icon: '↗️', 
        color: 'text-success', 
        text: 'A Acelerar', 
        rowClass: 'table-success',
        detail: `Crescimento a acelerar: ${shortTerm.toFixed(1)}% vs ${longTerm.toFixed(1)}%`
      };
    }
    if (bothPositive && !isImproving) {
      return { 
        icon: '↘️', 
        color: 'text-warning', 
        text: 'A Abrandar', 
        rowClass: 'table-warning',
        detail: `Crescimento a abrandar: ${shortTerm.toFixed(1)}% vs ${longTerm.toFixed(1)}%`
      };
    }
    
    // Ambos negativos
    if (bothNegative && isImproving) {
      return { 
        icon: '↗️', 
        color: 'text-info', 
        text: 'A Recuperar', 
        rowClass: 'table-info',
        detail: `Queda a diminuir: ${shortTerm.toFixed(1)}% vs ${longTerm.toFixed(1)}%`
      };
    }
    if (bothNegative && !isImproving) {
      return { 
        icon: '↘️', 
        color: 'text-danger', 
        text: 'A Piorar', 
        rowClass: 'table-danger',
        detail: `Queda a acelerar: ${shortTerm.toFixed(1)}% vs ${longTerm.toFixed(1)}%`
      };
    }
    
    // Mudança de sinal
    if (shortTerm > 0 && longTerm < 0) {
      return { 
        icon: '↗️', 
        color: 'text-success', 
        text: 'Positivo', 
        rowClass: 'table-success',
        detail: `Recuperação: ${shortTerm.toFixed(1)}% vs ${longTerm.toFixed(1)}%`
      };
    }
    if (shortTerm < 0 && longTerm > 0) {
      return { 
        icon: '↘️', 
        color: 'text-danger', 
        text: 'Negativo', 
        rowClass: 'table-danger',
        detail: `Reversão: ${shortTerm.toFixed(1)}% vs ${longTerm.toFixed(1)}%`
      };
    }
    
    // Fallback
    return { 
      icon: '→', 
      color: 'text-muted', 
      text: 'Indefinido', 
      rowClass: 'table-secondary',
      detail: `7d: ${shortTerm.toFixed(1)}% | 28d: ${longTerm.toFixed(1)}%`
    };
  };

  const getTrendColor = (value: number, isPrice: boolean = false) => {
    if (value === 0) return 'text-muted';
    if (isPrice) {
      // For prices: negative = good (green), positive = bad (red)
      if (value > 20) return 'text-danger';
      if (value > 10) return 'text-warning';
      if (value < -20) return 'text-success';
      if (value < -10) return 'text-info';
    } else {
      // For other metrics: positive = good (green), negative = bad (red)
      if (value > 20) return 'text-success';
      if (value > 10) return 'text-info';
      if (value < -20) return 'text-danger';
      if (value < -10) return 'text-warning';
    }
    return 'text-muted';
  };

  const exportToCSV = () => {
    if (performanceData.length === 0) return;

    const headers = [
      'SKU', 'Produto', 'Período',
      'Qtd Atual', 'Qtd Anterior', 'Var Qtd %',
      'Receita Atual', 'Receita Anterior', 'Var Receita %',
      'Vendas Atual', 'Vendas Anterior', 'Var Vendas %',
      'Preço Médio de Venda Atual', 'Preço Médio de Venda Anterior', 'Var Preço de Venda %',
      'Preço Médio Praticado Atual', 'Preço Médio Praticado Anterior', 'Var Preço Praticado %'
    ];

    const rows = performanceData.map(item => [
      item.sku,
      item.product_name || 'N/A',
      `${item.timeframe_days} dias`,
      item.current_period.quantity_sold,
      item.previous_period.quantity_sold,
      item.comparison.quantity_change.toFixed(1),
      item.current_period.total_revenue.toFixed(2),
      item.previous_period.total_revenue.toFixed(2),
      item.comparison.revenue_change.toFixed(1),
      item.current_period.sales_count,
      item.previous_period.sales_count,
      item.comparison.sales_change.toFixed(1),
      item.current_period.avg_sales_price?.toFixed(2) || '0.00',
      item.previous_period.avg_sales_price?.toFixed(2) || '0.00',
      item.comparison.sales_price_change.toFixed(1),
      item.current_period.avg_practiced_price?.toFixed(2) || '0.00',
      item.previous_period.avg_practiced_price?.toFixed(2) || '0.00',
      item.comparison.practiced_price_change.toFixed(1)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `performance_comparison_${timeframe}d_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="container-fluid px-3">
      <div className="row">
        <div className="col-12">
          <h1 className="mb-3">Comparação de Performance de Produtos</h1>
          
          {/* Input Form */}
          <div className="card mb-3">
            <div className="card-body py-3">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-9">
                    <label className="form-label mb-1">SKUs dos Produtos</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      placeholder="Insira os SKUs separados por vírgula ou um por linha"
                      value={skuInput}
                      onChange={(e) => setSkuInput(e.target.value)}
                      required
                    />
                    <small className="text-muted">
                      Exemplo: P075111, P075112, P075113 ou um SKU por linha
                    </small>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label mb-1">Modo de Visualização</label>
                      <div className="btn-group w-100" role="group">
                        <button
                          type="button"
                          className={`btn btn-sm ${viewMode === 'single' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setViewMode('single')}
                        >
                          Vista Única
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${viewMode === 'multi' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setViewMode('multi')}
                        >
                          Multi-Timeframe
                        </button>
                      </div>
                    </div>
                    {viewMode === 'single' && (
                      <div>
                        <label className="form-label mb-1">Período de Análise</label>
                        <div className="btn-group-vertical w-100" role="group">
                          {timeframeOptions.map(option => (
                            <button
                              key={option.value}
                              type="button"
                              className={`btn btn-sm ${timeframe === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setTimeframe(option.value)}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-2">
                  <button 
                    type="submit" 
                    className="btn btn-sm btn-success me-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {viewMode === 'multi' ? 'Analisando... (até 2 min)' : 'Carregando...'}
                      </>
                    ) : (
                      'Analisar Performance'
                    )}
                  </button>
                  {performanceData.length > 0 && (
                    <button 
                      type="button" 
                      className="btn btn-sm btn-secondary"
                      onClick={exportToCSV}
                    >
                      Exportar CSV
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Results Table - Single Timeframe */}
          {viewMode === 'single' && performanceData.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  Resultados da Comparação - {timeframe} dias
                </h5>
                <small className="text-muted">
                  Período Atual: {formatDate(performanceData[0]?.current_period.period_start)} - {formatDate(performanceData[0]?.current_period.period_end)} | 
                  Período Anterior: {formatDate(performanceData[0]?.previous_period.period_start)} - {formatDate(performanceData[0]?.previous_period.period_end)}
                </small>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th rowSpan={2} className="align-middle sticky-column">SKU</th>
                        <th rowSpan={2} className="align-middle">Produto</th>
                        <th colSpan={3} className="text-center border-start">Quantidade</th>
                        <th colSpan={3} className="text-center border-start">Receita</th>
                        <th colSpan={3} className="text-center border-start">N.º Vendas</th>
                        <th colSpan={3} className="text-center border-start">Preço Médio de Venda</th>
                        <th colSpan={3} className="text-center border-start">Preço Médio Praticado</th>
                      </tr>
                      <tr>
                        <th className="text-center border-start">Atual</th>
                        <th className="text-center">Anterior</th>
                        <th className="text-center">Var %</th>
                        <th className="text-center border-start">Atual</th>
                        <th className="text-center">Anterior</th>
                        <th className="text-center">Var %</th>
                        <th className="text-center border-start">Atual</th>
                        <th className="text-center">Anterior</th>
                        <th className="text-center">Var %</th>
                        <th className="text-center border-start">Atual</th>
                        <th className="text-center">Anterior</th>
                        <th className="text-center">Var %</th>
                        <th className="text-center border-start">Atual</th>
                        <th className="text-center">Anterior</th>
                        <th className="text-center">Var %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {performanceData.map((item, index) => (
                        <tr key={index}>
                          <td className="fw-bold sticky-column">
                            <a 
                              href={`/products/${item.sku}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-decoration-none"
                            >
                              {item.sku}
                            </a>
                          </td>
                          <td>{item.product_name || 'N/A'}</td>
                          {/* Quantidade */}
                          <td className="text-center border-start">{item.current_period.quantity_sold}</td>
                          <td className="text-center">{item.previous_period.quantity_sold}</td>
                          <td className="text-center">{formatPercentage(item.comparison.quantity_change)}</td>
                          {/* Receita */}
                          <td className="text-center border-start">{formatCurrency(item.current_period.total_revenue)}</td>
                          <td className="text-center">{formatCurrency(item.previous_period.total_revenue)}</td>
                          <td className="text-center">{formatPercentage(item.comparison.revenue_change)}</td>
                          {/* Vendas */}
                          <td className="text-center border-start">{item.current_period.sales_count}</td>
                          <td className="text-center">{item.previous_period.sales_count}</td>
                          <td className="text-center">{formatPercentage(item.comparison.sales_change)}</td>
                          {/* Preço Médio de Venda */}
                          <td className="text-center border-start">{formatCurrency(item.current_period.avg_sales_price)}</td>
                          <td className="text-center">{formatCurrency(item.previous_period.avg_sales_price)}</td>
                          <td className="text-center">{formatPricePercentage(item.comparison.sales_price_change)}</td>
                          {/* Preço Médio Praticado */}
                          <td className="text-center border-start">{formatCurrency(item.current_period.avg_practiced_price)}</td>
                          <td className="text-center">{formatCurrency(item.previous_period.avg_practiced_price)}</td>
                          <td className="text-center">{formatPricePercentage(item.comparison.practiced_price_change)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Results Table - Multi Timeframe */}
          {viewMode === 'multi' && multiTimeframeData.length > 0 && (
            <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">
                      Análise Multi-Timeframe - Detecção de Tendências
                    </h5>
                    <small className="text-muted">
                      Compare múltiplos períodos para identificar aceleração/desaceleração de vendas
                    </small>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <span className="badge bg-success">↗️ A Acelerar</span>
                    <span className="badge bg-warning">↘️ A Abrandar</span>
                    <span className="badge bg-info">↗️ A Recuperar</span>
                    <span className="badge bg-danger">↘️ A Piorar</span>
                    <span className="badge bg-secondary">→ Estável</span>
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th rowSpan={3} className="align-middle sticky-column">SKU</th>
                        <th rowSpan={3} className="align-middle">Produto</th>
                        <th rowSpan={3} className="align-middle">Tendência</th>
                        <th rowSpan={3} className="align-middle">Evolução (56d)</th>
                        <th colSpan={5} className="text-center border-start">Quantidade Vendida (Var %)</th>
                      </tr>
                      <tr>
                        <th className="text-center border-start">7d</th>
                        <th className="text-center">14d</th>
                        <th className="text-center">21d</th>
                        <th className="text-center">28d</th>
                        <th className="text-center">56d</th>
                      </tr>
                    </thead>
                    <tbody>
                      {multiTimeframeData.map((item, index) => {
                        const trend7d = item.timeframes['7d']?.quantity.change || 0;
                        const trend28d = item.timeframes['28d']?.quantity.change || 0;
                        const trendIndicator = getTrendIndicator(trend7d, trend28d);
                        
                        return (
                          <tr key={index} className={trendIndicator.rowClass}>
                            <td className="fw-bold sticky-column">
                              <a 
                                href={`/products/${item.sku}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-decoration-none"
                              >
                                {item.sku}
                              </a>
                            </td>
                            <td>{item.product_name || 'N/A'}</td>
                            <td className="text-center">
                              <span 
                                title={trendIndicator.detail}
                                className={trendIndicator.color}
                                style={{ cursor: 'help' }}
                              >
                                {trendIndicator.icon} {trendIndicator.text}
                              </span>
                            </td>
                            
                            {/* Sparkline */}
                            <td className="text-center">
                              <SparklineChart 
                                data={sparklineData[item.sku] || []} 
                                width={80} 
                                height={30} 
                              />
                            </td>
                            
                            {/* Quantidade */}
                            {['7d', '14d', '21d', '28d', '56d'].map(period => {
                              const data = item.timeframes[period];
                              const change = data?.quantity.change || 0;
                              return (
                                <td key={`qty-${period}`} className={`text-center ${period === '7d' ? 'border-start' : ''}`}>
                                  <div className="small">{data?.quantity.current || 0}</div>
                                  <div className={`small ${getTrendColor(change)}`}>
                                    {change > 0 ? '+' : ''}{change.toFixed(1)}%
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceComparisonPage;